import { Card as AntdCard } from 'antd';
import React, { useState } from 'react';
import { colors } from '../colors';
import { Tabs } from '../Tab';
import { withTheme } from '../ThemeProvider';
import { StyledCard } from './styles';
import { CardMetaProps, CardProps } from './types';

function CardComponent({
  title,
  extra,
  children,
  cover,
  actions,
  hoverable = false,
  loading = false,
  variant = 'outlined',
  size = 'default',
  style,
  className,
  fullWidth = false,
  tabList,
  activeTabKey,
  tabBarExtraContent,
  onTabChange,
  tabProps,
  icon,
  iconPosition = 'left',
  type = 'default',
  headerBgColor = colors['so-blue-0.04'],
  showHeader = true,
  ...props
}: CardProps) {
  const [activeKey, setActiveKey] = useState<string | undefined>(activeTabKey);

  const handleTabChange = (key: string) => {
    setActiveKey(key);
    if (onTabChange) {
      onTabChange(key);
    }
  };

  // Create a custom title with icon if provided
  const customTitle = icon ? (
    <div className="card-title-with-icon">
      {iconPosition === 'left' && <span className="card-title-icon">{icon}</span>}
      <span>{title}</span>
      {iconPosition === 'right' && <span className="card-title-icon">{icon}</span>}
    </div>
  ) : (
    title
  );

  return (
    <StyledCard
      title={showHeader ? customTitle : undefined}
      extra={showHeader ? extra : undefined}
      cover={cover}
      actions={actions}
      hoverable={hoverable}
      loading={loading}
      variant={variant}
      size={size}
      style={style}
      className={className}
      fullWidth={fullWidth}
      tabBarExtraContent={tabBarExtraContent}
      isInnerCard={type === 'inner'}
      headerBgColor={headerBgColor}
      {...props}
    >
      {tabList ? (
        <>
          <Tabs
            activeKey={activeKey}
            onChange={handleTabChange}
            items={tabList.map(item => ({
              key: item.key,
              label: item.tab || item.label,
            }))}
            {...tabProps}
          />
          {children}
        </>
      ) : (
        children
      )}
    </StyledCard>
  );
}

const CardWithTheme = withTheme(CardComponent);

// Create Meta component
const Meta: React.FC<CardMetaProps> = ({
  avatar,
  title,
  description,
  className,
  style,
  ...props
}) => (
  <AntdCard.Meta
    avatar={avatar}
    title={title}
    description={description}
    className={className}
    style={style}
    {...props}
  />
);

// Create final Card component with Grid and Meta as properties
type CardType = typeof CardWithTheme & {
  Grid: typeof AntdCard.Grid;
  Meta: typeof Meta;
};

const Card = CardWithTheme as CardType;
Card.Grid = AntdCard.Grid;
Card.Meta = Meta;

export { Card };
